import { trackDevice } from '@maxmind/device-tracking';

const TRACKING_TOKEN_STORAGE_KEY = 'maxmind-tracking-token';

let trackingPromise: Promise<string> | null = null;

const parseAccountId = (value: string) => {
    if (!/^\d+$/.test(value)) {
        return 0;
    }

    const accountId = Number(value);

    if (!Number.isFinite(accountId) || accountId <= 0) {
        return 0;
    }

    return accountId;
};

const getAccountId = () => {
    const value = `${process.env.REACT_APP_MAXMIND_ACCOUNT_ID || ''}`.trim();

    return parseAccountId(value);
};

const requestTrackingToken = async (accountId: number): Promise<string> => {
    try {
        const result = await trackDevice({
            accountId,
        });

        const trackingToken = (result?.trackingToken || '').trim();

        if (trackingToken) {
            window.sessionStorage.setItem(TRACKING_TOKEN_STORAGE_KEY, trackingToken);
        }

        return trackingToken;
    } catch {
        return window.sessionStorage.getItem(TRACKING_TOKEN_STORAGE_KEY) || '';
    }
};

export const initializeMaxMindTracking = async (): Promise<string> => {
    if (typeof window === 'undefined') {
        return '';
    }

    const cachedToken = window.sessionStorage.getItem(TRACKING_TOKEN_STORAGE_KEY);

    if (cachedToken) {
        return cachedToken;
    }

    const accountId = getAccountId();

    if (!accountId) {
        return '';
    }

    if (trackingPromise) {
        return trackingPromise;
    }

    trackingPromise = requestTrackingToken(accountId)
        .finally(() => {
            trackingPromise = null;
        });

    return trackingPromise;
};

export const getMaxMindTrackingToken = async (): Promise<string> => {
    if (typeof window === 'undefined') {
        return '';
    }

    const cachedToken = window.sessionStorage.getItem(TRACKING_TOKEN_STORAGE_KEY);

    if (cachedToken) {
        return cachedToken;
    }

    const token = await initializeMaxMindTracking();

    if (token) {
        return token;
    }

    // Retry once to avoid sticky empty tokens when the first script load fails.
    return initializeMaxMindTracking();
};